import React, { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiExternalLink, FiSave, FiCheckCircle, FiAlertCircle, FiShare2, FiCopy } = FiIcons;

const QRCodeCard = ({ code, onUpdate }) => {
  const [url, setUrl] = useState(code.destinationUrl);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const qrRef = useRef();

  const formatAndValidateUrl = (input) => {
    let formatted = input.trim();
    if (!formatted) return null;
    
    if (!/^https?:\/\//i.test(formatted)) {
      formatted = `https://${formatted}`;
    }

    try {
      new URL(formatted);
      return formatted;
    } catch (e) {
      return null;
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setValidationError(false);
    
    const formattedUrl = formatAndValidateUrl(url);
    
    if (!formattedUrl) {
      setValidationError(true);
      return;
    }

    setIsSaving(true);
    try {
      await onUpdate(code.id, formattedUrl);
      setUrl(formattedUrl);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = () => {
    const svg = qrRef.current.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      const scale = 10;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const pngFile = canvas.toDataURL("image/png", 1.0);
      const downloadLink = document.createElement("a");
      downloadLink.download = `etera-qr-${code.slug}.png`;
      downloadLink.href = `${pngFile}`;
      downloadLink.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  const redirectUrl = `${window.location.origin}/q/${code.slug}`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'ETERA Permanent Link',
          text: 'Check out my permanent QR link:',
          url: redirectUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(redirectUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <div className="bg-white border-[3px] border-black p-4 md:p-6 brutal-shadow flex flex-col md:flex-row gap-6 w-full max-w-full overflow-hidden">
      {/* LEFT COLUMN: QR Code & Download */}
      <div className="flex flex-col items-center gap-3 shrink-0">
        <div className="border-[3px] border-black p-3 bg-white" ref={qrRef}>
          <QRCodeSVG value={redirectUrl} size={140} level="H" includeMargin={false} />
        </div>
        <button 
          onClick={handleDownload}
          className="flex items-center justify-center gap-2 w-full border-[3px] border-black py-2 text-sm font-black uppercase hover:bg-black hover:text-white transition-colors"
        >
          <SafeIcon icon={FiDownload} className="w-4 h-4" />
          Download PNG
        </button>
      </div>

      {/* RIGHT COLUMN: Details & Actions */}
      <div className="flex flex-col flex-grow justify-between min-w-0 text-left">
        <div className="mb-4">
          <div className="flex justify-between items-start mb-1">
            <p className="text-[10px] font-black uppercase text-gray-400 tracking-wider">Permanent Short Link</p>
            <div className="flex gap-2">
               <button 
                onClick={handleShare}
                className={`p-1 border-2 border-black transition-colors ${copied ? 'bg-green-400' : 'hover:bg-yellow-300'}`}
                title={copied ? "Copied!" : "Share Link"}
              >
                <SafeIcon icon={copied ? FiCheckCircle : FiShare2} className="w-4 h-4" />
              </button>
              <button 
                onClick={() => window.open(redirectUrl, '_blank')}
                className="p-1 border-2 border-black hover:bg-black hover:text-white transition-colors"
                title="Open Link"
              >
                <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <div className="font-black text-2xl sm:text-3xl truncate mb-4">
            {window.location.host}/q/{code.slug}
          </div>

          <div className="flex flex-wrap gap-2 text-xs font-black uppercase">
            <div className="border-[3px] border-black flex items-stretch">
              <span className="bg-black text-white px-2 py-1">Scans</span>
              <span className="px-2 py-1 bg-white text-black">{code.scans || 0}</span>
            </div>
            <div className="border-[3px] border-black bg-white text-black px-2 py-1">
              Created {code.createdAt || 'N/A'}
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="flex flex-col gap-1 mt-auto w-full">
          <div className="flex justify-between items-center">
            <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider">Destination URL</label>
            {validationError && (
              <span className="text-[10px] font-black uppercase text-red-600 flex items-center gap-1">
                <SafeIcon icon={FiAlertCircle} /> Invalid URL
              </span>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <input 
              type="text" 
              required 
              value={url} 
              onChange={(e) => {
                setUrl(e.target.value);
                setValidationError(false);
              }}
              className={`flex-grow min-w-0 border-[3px] p-2 font-bold focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 text-sm ${
                validationError ? 'border-red-600 bg-red-50' : 'border-black'
              }`}
              placeholder="example.com"
            />
            <button 
              type="submit" 
              disabled={isSaving}
              className={`shrink-0 flex items-center justify-center gap-2 px-6 py-2 text-sm font-black uppercase border-[3px] border-black brutal-hover transition-colors ${
                saved ? 'bg-green-400 text-black' : 'bg-black text-white hover:bg-white hover:text-black'
              }`}
            >
              {isSaving ? '...' : saved ? <><SafeIcon icon={FiCheckCircle} className="w-4 h-4"/> Saved</> : <><SafeIcon icon={FiSave} className="w-4 h-4"/> Update</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QRCodeCard;